import * as Sentry from "@sentry/vue";
import { Integrations } from "@sentry/tracing";

const dsn = process.env.VUE_APP_TOKEN_SENTRY;
const environment = process.env.VUE_APP_NODE_ENV || process.env.NODE_ENV;
const isProduction = environment === "production";

// Sentry solo se activa si hay DSN configurado, para no ensuciar el
// proyecto con eventos de entornos locales sin credenciales.
const enabled = Boolean(dsn);

// Los crawlers no cargan recursos de terceros (analytics, fuentes), asi que
// generan errores que ningun usuario real sufre. Se descartan enteros: sus
// eventos solo gastan cuota y ensucian el monitor.
const BOT_USER_AGENT =
  /bot|crawler|spider|crawling|Google-Read-Aloud|Chrome-Lighthouse|HeadlessChrome|PageSpeed/i;

const isBot = () =>
  typeof navigator !== "undefined" && BOT_USER_AGENT.test(navigator.userAgent);

const initSentry = (app, router) => {
  if (!enabled) {
    return;
  }

  Sentry.init({
    app,
    dsn,
    environment,
    release: process.env.VUE_APP_SENTRY_RELEASE,
    // Reporta al log del navegador ademas de enviarlo a Sentry
    logErrors: true,
    integrations: [
      new Integrations.BrowserTracing({
        routingInstrumentation: Sentry.vueRouterInstrumentation(router),
        tracingOrigins: [
          "localhost",
          process.env.VUE_APP_BASE_URL,
          process.env.VUE_APP_FRONT_BASE_URL,
          /^\//,
        ].filter(Boolean),
      }),
    ],
    // En produccion se muestrea para no consumir la cuota; en el resto
    // de entornos interesa ver todas las transacciones.
    tracesSampleRate: isProduction ? 0.2 : 1.0,
    // Ruido del navegador. Los errores de red NO se filtran aqui: el
    // interceptor de axios ya decide cuales vale la pena reportar, y
    // filtrarlos por mensaje descartaria tambien esos.
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
      // vue-gtag-next inyecta el <script> de Google Analytics y rechaza la
      // promesa con el Event del DOM si no carga, sin .catch() detras. Un
      // ad-blocker o una red que filtre googletagmanager basta para dispararlo
      // y la app funciona igual, solo se pierde el tracking de esa visita.
      // El juego exacto de claves es el de un Event de error, no cualquier
      // rechazo sin Error: filtrar por el mensaje entero evita tragarse otros.
      /Non-Error promise rejection captured with keys: currentTarget, isTrusted, target, type/,
    ],
    denyUrls: [/extensions\//i, /^chrome:\/\//i, /^chrome-extension:\/\//i],
    beforeSend(event) {
      // En la v6 beforeSend tambien recibe las transacciones, asi que esto
      // corta de raiz errores y trazas de los crawlers.
      if (isBot()) {
        return null;
      }

      // No enviar tokens ni credenciales si viajaran en la URL
      if (event.request?.url) {
        event.request.url = event.request.url.replace(
          /([?&](token|access_token|refresh_token|jwt)=)[^&]+/gi,
          "$1[Filtered]"
        );
      }

      return event;
    },
  });
};

const setSentryUser = (user) => {
  if (!enabled || !user) {
    return;
  }

  Sentry.setUser({
    id: user.id,
    username: user.name,
    email: user.email,
  });
};

const clearSentryUser = () => {
  if (!enabled) {
    return;
  }

  Sentry.setUser(null);
};

// Devuelve el id del evento en Sentry, o null si esta desactivado
const captureException = (error, context = {}) => {
  if (!enabled) {
    console.error(error);
    return null;
  }

  return Sentry.captureException(error, { extra: context });
};

const captureMessage = (message, context = {}, level = "info") => {
  if (!enabled) {
    console.log(message, context);
    return null;
  }

  return Sentry.captureMessage(message, { level, extra: context });
};

export {
  Sentry,
  initSentry,
  setSentryUser,
  clearSentryUser,
  captureException,
  captureMessage,
  environment as sentryEnvironment,
  enabled as sentryEnabled,
};
